import { useState, useEffect, useCallback } from 'react';
import { Loader2, MessageCircle, UserX, RefreshCw, Phone, Mail, Tag, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CrmInbox() {
  const [data, setData] = useState({ items: [], replies_count: 0, unsubscribes_count: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | reply | unsubscribe

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/crm/feedback`);
      const d = await r.json();
      setData(d);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const items = data.items.filter(it => filter === 'all' ? true : it.type === filter);

  const fmt = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  return (
    <div className="space-y-4" data-testid="crm-inbox">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[rgb(var(--lm-text))]">Boîte retour CRM</h2>
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">Réponses prospects · Désinscriptions</p>
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-1.5 text-xs px-3 h-8 rounded-lg border border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text-muted))] hover:bg-[rgb(var(--lm-bg))]"
          data-testid="inbox-refresh">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Actualiser
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-2">
        <button onClick={() => setFilter(filter === 'reply' ? 'all' : 'reply')}
          className={`text-left p-3 rounded-xl border transition-all ${filter === 'reply' ? 'bg-emerald-50 border-emerald-300' : 'bg-[rgb(var(--lm-card))] border-[rgb(var(--lm-border))]/30 hover:border-emerald-200'}`}
          data-testid="filter-replies">
          <div className="flex items-center gap-2 mb-1">
            <MessageCircle size={14} className="text-emerald-600" />
            <span className="text-[10px] uppercase tracking-wider text-[rgb(var(--lm-text-muted))]">Réponses reçues</span>
          </div>
          <p className="text-2xl font-bold text-emerald-700 font-mono">{data.replies_count}</p>
          <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">prospects qui ont répondu</p>
        </button>

        <button onClick={() => setFilter(filter === 'unsubscribe' ? 'all' : 'unsubscribe')}
          className={`text-left p-3 rounded-xl border transition-all ${filter === 'unsubscribe' ? 'bg-rose-50 border-rose-300' : 'bg-[rgb(var(--lm-card))] border-[rgb(var(--lm-border))]/30 hover:border-rose-200'}`}
          data-testid="filter-unsubscribes">
          <div className="flex items-center gap-2 mb-1">
            <UserX size={14} className="text-rose-600" />
            <span className="text-[10px] uppercase tracking-wider text-[rgb(var(--lm-text-muted))]">Désinscriptions</span>
          </div>
          <p className="text-2xl font-bold text-rose-700 font-mono">{data.unsubscribes_count}</p>
          <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">contacts opt-out RGPD</p>
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-[rgb(var(--lm-text-muted))]" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 bg-[rgb(var(--lm-card))] rounded-xl border border-[rgb(var(--lm-border))]/30">
          <MessageCircle size={32} className="text-[rgb(var(--lm-text-muted))]/20 mx-auto mb-3" />
          <p className="text-sm text-[rgb(var(--lm-text-muted))]">Aucun retour</p>
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-1">Les réponses et désinscriptions apparaîtront ici</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i}
              className={`bg-[rgb(var(--lm-card))] border rounded-xl p-3 ${it.type === 'reply' ? 'border-emerald-200' : 'border-rose-200'}`}
              data-testid={`inbox-item-${i}`}>
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${it.type === 'reply' ? 'bg-emerald-100' : 'bg-rose-100'}`}>
                  {it.type === 'reply'
                    ? <MessageCircle size={16} className="text-emerald-700" />
                    : <UserX size={16} className="text-rose-700" />}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-bold text-[rgb(var(--lm-text))]">{it.nom}</p>
                    <span className={`text-[8px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded-full ${it.type === 'reply' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                      {it.type === 'reply' ? '📩 Réponse' : '🚫 Désinscrit'}
                    </span>
                    {it.statut === 'rdv' && (
                      <span className="text-[8px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
                        🔥 RDV
                      </span>
                    )}
                  </div>

                  {it.contact && it.contact !== it.nom && (
                    <p className="text-[11px] text-[rgb(var(--lm-text-muted))] mt-0.5">{it.contact}</p>
                  )}

                  <div className="flex items-center gap-3 mt-1.5 text-[10px] text-[rgb(var(--lm-text-muted))] flex-wrap">
                    {it.email && (
                      <a href={`mailto:${it.email}`} className="flex items-center gap-1 hover:text-[rgb(var(--lm-gold))]">
                        <Mail size={10} /> {it.email}
                      </a>
                    )}
                    {it.telephone && (
                      <a href={`tel:${it.telephone}`} className="flex items-center gap-1 hover:text-[rgb(var(--lm-gold))]">
                        <Phone size={10} /> {it.telephone}
                      </a>
                    )}
                    {it.secteur && (
                      <span className="flex items-center gap-1">
                        <Tag size={10} /> {it.secteur}
                      </span>
                    )}
                    <span className="ml-auto font-mono">{fmt(it.when)}</span>
                  </div>

                  {it.snippet && (
                    <div className="mt-2 px-2.5 py-1.5 bg-[rgb(var(--lm-bg))] rounded-lg text-[11px] text-[rgb(var(--lm-text-secondary))] italic border-l-2 border-emerald-300">
                      « {it.snippet} »
                    </div>
                  )}

                  {it.type === 'reply' && it.id && (
                    <a
                      href={`/patron?prospect=${it.id}`}
                      className="inline-flex items-center gap-1 mt-2 text-[10px] text-[rgb(var(--lm-gold))] font-bold hover:underline"
                      data-testid={`open-prospect-${it.id}`}>
                      Ouvrir la fiche prospect <ExternalLink size={9} />
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
